import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/pages/AppShell";
import { v1 } from "@/lib/api";
import { useOrg, hasRole } from "@/lib/orgContext";
import { Trash, UserPlus } from "iconoir-react";

const ROLES = ["admin", "manager", "editor", "viewer", "client"];

export default function OrgMembers() {
  const { orgId } = useParams();
  const { memberships, refresh } = useOrg();
  const myMembership = memberships.find((m) => m.organization.id === orgId);
  const myRole = myMembership?.role || "viewer";
  const canManage = hasRole(myRole, "admin");

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("viewer");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await v1.members.list(orgId);
      setMembers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!userId.trim()) return;
    setAdding(true);
    try {
      await v1.members.add(orgId, { user_id: userId.trim(), role });
      toast.success("Member added");
      setUserId("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add member");
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (uid, newRole) => {
    try {
      await v1.members.setRole(orgId, uid, newRole);
      toast.success("Role updated");
      await load();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemove = async (uid) => {
    if (!confirm("Remove this member?")) return;
    try {
      await v1.members.remove(orgId, uid);
      toast.success("Member removed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove member");
    }
  };

  return (
    <AppShell title="Members" subtitle={myMembership?.organization?.name || "ORGANIZATION"}>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3" data-testid="members-list">
          {loading && <div className="font-mono text-xs text-muted-foreground">Loading…</div>}
          {!loading && members.length === 0 && (
            <div className="border border-border p-6 text-sm text-muted-foreground">No members.</div>
          )}
          {members.map((m) => (
            <div
              key={m.user_id}
              data-testid={`member-row-${m.user_id}`}
              className="border border-border p-4 flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="font-app text-sm truncate">{m.email || m.user_id}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {m.business_name || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {m.role === "owner" ? (
                  <span
                    data-testid={`member-role-owner-${m.user_id}`}
                    className="px-3 py-1 bg-intl text-bone text-[10px] font-mono uppercase tracking-widest"
                  >
                    owner
                  </span>
                ) : (
                  <select
                    value={m.role}
                    onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                    disabled={!canManage}
                    data-testid={`member-role-select-${m.user_id}`}
                    className="border border-border bg-background text-xs font-app px-2 py-1"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                )}
                {canManage && m.role !== "owner" && (
                  <button
                    onClick={() => handleRemove(m.user_id)}
                    data-testid={`member-remove-${m.user_id}`}
                    className="p-1.5 border border-border hover:bg-card"
                    title="Remove"
                  >
                    <Trash width={14} height={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {canManage && (
          <form
            onSubmit={handleAdd}
            data-testid="member-add-form"
            className="border border-border p-5 space-y-3 h-fit"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Invite by user_id</div>
            <input
              type="text"
              placeholder="user_id (Mongo ObjectId)"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              data-testid="member-add-userid"
              className="w-full border border-border px-3 py-2 bg-background text-sm font-app"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="member-add-role"
              className="w-full border border-border px-3 py-2 bg-background text-sm font-app"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={adding}
              data-testid="member-add-submit"
              className="w-full flex items-center justify-center gap-2 py-2 bg-obsidian text-bone text-xs font-app uppercase tracking-widest disabled:opacity-60"
            >
              <UserPlus width={14} height={14} /> {adding ? "Adding…" : "Add member"}
            </button>
            <p className="font-mono text-[10px] text-muted-foreground">
              Phase 1: invite-by-email pending. Use the user’s Mongo ObjectId for now.
            </p>
          </form>
        )}
      </div>
    </AppShell>
  );
}
